import styled from "styled-components";
import React, { useState, useEffect, useRef} from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { blue, darkRed, red } from "../../styles";
import {
  faSpinner,
  faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import "./ToxicologistSearch.css";
import { searchAbstracts } from "../../services/toxicologistSearchAPI";
import DOMPurify from "dompurify";

// helpers for rendering
const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const formatTime = (t) => (t ? t.toString().slice(0,5) : ""); // "HH:MM"
const truncate = (t, n = 600) => (t && t.length > n ? t.slice(0, n) + "…" : t || "");


/** Decode HTML entities first (e.g., &micro;, &#181;) */
const decodeEntities = (s) => {
  if (!s) return "";
  const el = document.createElement("textarea");
  el.innerHTML = String(s);
  return el.value;
};

// true if there's actual text after stripping all tags/entities/nbsp
const hasText = (html) => {
  if (!html) return false;
  const stripped = DOMPurify.sanitize(String(html), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const plain = decodeEntities(stripped).replace(/\u00a0|&nbsp;/gi, ' ').trim();
  return plain.length > 0;
};

/** Heuristic: does the string look like UTF-8 bytes misread as Latin-1? */
const looksLikeUtf8Mojibake = (s) => /[ÃÂâ]/.test(s) && !/\uFFFD/.test(s);

/** Try to re-decode Latin-1 bytes as UTF-8 (fixes Ã©, â€™, â€œ, â€” , â‰ , etc.) */
const fixUtf8Mojibake = (s) => {
  try {
    // Convert each code unit (0–255) to a byte, then decode as UTF-8.
    // Run up to twice to catch double-encoded sequences like "ÃƒÂ©".
    let out = s;
    for (let i = 0; i < 2; i++) {
      // If any code unit > 255, this trick isn't safe; bail.
      if ([...out].some(ch => ch.charCodeAt(0) > 255)) break;
      const bytes = new Uint8Array([...out].map(ch => ch.charCodeAt(0) & 0xFF));
      const dec = new TextDecoder("utf-8", { fatal: false });
      const decoded = dec.decode(bytes);
      if (decoded === out) break;
      out = decoded;
    }
    return out;
  } catch {
    // Fallback (older trick); may help in some browsers.
    try { return decodeURIComponent(escape(s)); } catch { return s; }
  }
};

/** Fix common mojibake + replacement-char cases, preserving real symbols */
const normalizeWeirdText = (s) => {
  if (!s) return "";
  let out = decodeEntities(String(s)).normalize("NFC");

  // Broad fix: re-decode UTF-8 that was misread as Latin-1 (Ã© → é, â€™ → ’, â€œ → “, â€” → —, â‰  → ≠, etc.)
  if (looksLikeUtf8Mojibake(out)) out = fixUtf8Mojibake(out);

  // Targeted cleanups for leftovers we still see in the wild
  const pairs = [
    [/Â±/g, "±"], [/Âµ/g, "µ"], [/Â°/g, "°"], [/Â·/g, "·"], [/Ã—/g, "×"],
    // If any ≠ survived
    [/â‰\s*Â?/g, "≠"],
  ];
  for (const [re, rep] of pairs) out = out.replace(re, rep);

   // Heuristics for when original bytes were truly lost and got replaced with �:
  // number � number  -> number ± number
  out = out.replace(/(\d[\d.,]*)\s*\uFFFD\s*([\d.,]+)/g, "$1±$2");
  // �C / �F -> °C / °F
  out = out.replace(/\uFFFD\s*([cCfF])\b/g, (_, u) => `°${u.toUpperCase()}`);

  return out;
};



/** Sanitize minimal inline HTML AFTER normalization */
const sanitizeRich = (html) =>
  DOMPurify.sanitize(normalizeWeirdText(html), {
    ALLOWED_TAGS: ["b","strong","i","em","u","sup","sub","span","br","p"],
    ALLOWED_ATTR: []
  });



// very small, safe allowlists
const sanitizeInline = (html) =>
  DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: ["b","i","em","strong","u","sup","sub","span","br"],
    ALLOWED_ATTR: [], // keep empty unless you truly need inline styles
  });


// Title Case (preserves ACRONYMS & small words)
const toTitleCase = (str) => {
  if (!str) return "";
  const SMALL = new Set(["a","an","and","as","at","but","by","for","in","nor","of","on","or","per","the","to","vs","via"]);
  const orig = String(str).split(/\s+/);
  const words = String(str).toLowerCase().split(/\s+/);
  const cap = (w) => (w ? w[0].toUpperCase() + w.slice(1) : w);

  return words.map((w, i) => {
    const raw = orig[i] || w;
    if (raw.length > 1 && raw === raw.toUpperCase() && /[A-Z0-9]/.test(raw)) return raw; // ACRONYM
    const firstOrLast = i === 0 || i === words.length - 1;
    const afterPunct = i > 0 && /[:–—-]$/.test(orig[i-1] || "");
    if (!firstOrLast && !afterPunct && SMALL.has(w)) return w;
    if (w.includes("-")) {
      return w.split("-").map((seg, j, a) =>
        (!SMALL.has(seg) || j === 0 || j === a.length-1) ? cap(seg) : seg
      ).join("-");
    }
    return cap(w);
  }).join(" ");
};

//helper to sanitize then title case
const sanitizeAndTitleCaseHTML = (html) => {
  if (!html) return "";

  // 1) Sanitize: allow only a tiny, safe tag set
  const clean = DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS: ["i", "em", "b", "strong", "sup", "sub", "span"],
    ALLOWED_ATTR: [] // keep this strict
  });

  // 2) Parse and Title Case text nodes only
  const parser = new DOMParser();
  const doc = parser.parseFromString(clean, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

  let node;
  while ((node = walker.nextNode())) {
    node.nodeValue = toTitleCase(node.nodeValue);
  }
  return doc.body.innerHTML;
};


const getActiveFilters = (fd) => {
  const out = [];
  if (fd?.abstractNumber?.trim()) out.push(["Abstract Number", fd.abstractNumber.trim()]);
  if (fd?.year) out.push(["Year", fd.year]);
  if (fd?.author?.trim()) out.push(["Author", fd.author.trim()]);
  if (fd?.title?.trim()) out.push(["Title", fd.title.trim()]);
  if (fd?.keywords?.trim()) out.push(["Keywords", fd.keywords.trim()]);
  if (fd?.abstractBody?.trim()) out.push(["Abstract Body", fd.abstractBody.trim()]);
  return out;
};




//console.log("📦 Loaded: ToxicologistSearch.js from components folder file");

export default function ToxicologistSearch() {
  console.log("🟢 Rendering: ToxicologistSearch");
  const [formData, setFormData] = useState({
    abstractNumber: "",
    year: "",
    author: "",
    title: "",
    keywords: "",
    abstractBody: "",
    hits: 10,
    sort: "newest"
  });
  const HITS_PER_PAGE = 10;

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('year_desc');   // 'year_desc' (default) | 'year_asc'

 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]); 
  const [submittedFilters, setSubmittedFilters] = useState(null);
  const [shouldScrollToResults, setShouldScrollToResults] = useState(false);
  const resultsTopRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


const handleSubmit = async (e) => {
  e.preventDefault();
  setShouldScrollToResults(true);
  setError("");
  setResults([]);
  setLoading(true);



  // 🔎 If the user didn’t enter anything, show message and stop
  const noFilters =
    !formData.abstractNumber?.trim() &&
    !formData.author?.trim() &&
    !formData.title?.trim() &&
    !formData.keywords?.trim() &&
    !formData.abstractBody?.trim() &&
    formData.year === ""; 

  if (noFilters) {
    setLoading(false);
    setError("No abstracts found");
    return;
  }

  try {
    const payload = {
      // send only the filters (built from your formData/submittedFilters)
      abstractNumber: formData.abstractNumber || "",
      year: formData.year && formData.year !== "Select a year" ? Number(formData.year) : null,
      author: formData.author || "",
      title: formData.title || "",
      keywords: formData.keywords || "",
      abstractBody: formData.abstractBody || "",
      page: 1,
      hits: HITS_PER_PAGE,
      sort
    };
    
    /*
    const data = await searchAbstracts({
      abstractNumber: formData.abstractNumber?.trim(),
      year: formData.year,
      author: formData.author?.trim(),
      title: formData.title?.trim(),
      keywords: formData.keywords?.trim(),
      abstractBody: formData.abstractBody?.trim(),
      hits: Number(formData.hits) || 10,
      sort: formData.sort || "newest",
      page: 1, 
    });*/
    const data = await searchAbstracts(payload);
      if (data?.message) {
        setResults([]);
        setTotal(0);
        setSubmittedFilters({ ...formData });
        setPage(1);
        setError("No abstracts found.");
      } else {
        setResults(data.items || []);
        setTotal(data.total || 0);
        setSubmittedFilters({ ...formData });
        setPage(data.page || 1);
      }
    
    console.log("API const data:", data); // TEMP: helps confirm shape

    if (Array.isArray(data)) {
      // Old shape (array-only): keep working just in case
      setResults(data);
    } else if (data && Array.isArray(data.items)) {
      // New shape (paged object)
      setResults(data.items);            // <- important line
      // (optional) if you want to show totals later:
      // setTotal(data.total);
      // setPage(data.page);
      // setHits(data.hits);
      setSubmittedFilters({ ...formData }); // freeze what the user searched
    } else if (data?.message) {
      setError(data.message);
    } else {
      setError("Unexpected response from server.");
    }
  } catch (err) {
    setError(err.message || "Something went wrong.");
  } finally {
    setLoading(false);
  }
};
  
useEffect(() => {
  if (!shouldScrollToResults) return;
  if (loading) return; // wait until API finishes

  // Scroll if we have either results or an error message to show
  if (resultsTopRef.current && (results.length > 0 || error)) {
    resultsTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    setShouldScrollToResults(false);
  }
}, [shouldScrollToResults, loading, results.length, error]);

  return (
    <div className="section-content p-6">

      <p>
        <em>The Toxicologist</em> and{" "}
        <em>The Toxicologist: Late Breaking Supplement</em> are supplements to{" "}
        <em>Toxicological Sciences</em>, the Society’s official journal, and
        contain all abstracts presented at the SOT Annual Meeting and ToxExpo.
        This online database contains all abstracts published in{" "}
        <em>The Toxicologist</em> starting with the 2003 Annual Meeting.
      </p>
      <p>
        In order to cite an abstract from an SOT Annual Meeting and ToxExpo,
        please use one of the following formats:
      </p>
      <ol>
        <li>
          [Authors]. [Meeting Year]. “[Abstract Title].”{" "}
          <em>The Toxicologist</em>, a Supplement to{" "}
          <em>Toxicological Sciences</em>, Abstract #[Four-Digit Abstract
          Number].
          <br />
          <strong>Example:</strong> J. Doe. 2022. “The Study of Poisons.”{" "}
          <em>The Toxicologist</em>, a Supplement to{" "}
          <em>Toxicological Sciences</em>, Abstract #2023.
        </li>
        <li>
          [Authors]. [Meeting Year]. “[Abstract Title].”{" "}
          <em>The Toxicologist: Late-Breaking Supplement</em>, a Supplement to{" "}
          <em>Toxicological Sciences</em>, Abstract #[Four-Digit Abstract
          Number].
          <br />
          <strong>Example:</strong> J. Smith. 2022. “The Toxicology of Poisons.”{" "}
          <em>The Toxicologist: Late-Breaking Supplement</em>, a Supplement to{" "}
          <em>Toxicological Sciences</em>, Abstract #2024.
        </li>
      </ol>

      <h2 className="">Toxicologist Database Search Options</h2>

      <form onSubmit={handleSubmit}>
          <fieldset>
          <div className="form-row">  
            <div className="form-group form-column-keywords">
            <label htmlFor="txtKeywords">Keyword(s)</label>
            <input
              type="text"
              id="txtKeywords"
              name="keywords"                  
              className="form-control"
              value={formData.keywords}
              onChange={handleChange}
            />            
          </div>
          <div className="form-group form-column-abstract">
            <label htmlFor="txtAbstractNumber">Abstract Number</label>
            <input
              type="text"
              id="txtAbstractNumber"
              name="abstractNumber"
              className="form-control"
              value={formData.abstractNumber}
              onChange={handleChange}
              inputMode="numeric"
              pattern="\d{3,}"
              title="Please enter at least 3 digits"
            />
          </div>
          
          <div className="form-group form-column-year custom-select-wrapper">
            <label htmlFor="ddlAbstractYear">Year</label>
            <select
              id="ddlAbstractYear"
              name="year"
              className="form-control"
              value={formData.year}
              onChange={handleChange}
            >
              <option value="">Select a year</option>
              {Array.from({ length: 2026 - 2003 + 1 }, (_, i) => 2003 + i)
                .reverse()
                .map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
            </select>
            <FontAwesomeIcon icon={faChevronDown} className="select-icon" />
          </div>
          </div>
          
          <div className="form-row">  
          <div className="form-group form-column-title">
            <label htmlFor="txtTitle">Title</label>
            <input
              type="text"
              id="txtTitle"
              name="title"                     
              className="form-control"
              value={formData.title}
              onChange={handleChange}
            />            
          </div>

          <div className="form-group form-column-authors">
            <label htmlFor="txtAuthor1">Author(s)</label>
           <input
              type="text"
              id="txtAuthor1"
              name="author"                    
              className="form-control"
              value={formData.author}
              onChange={handleChange}
            />
          </div>

         
          </div>
         

          

          <div className="form-group">
            <label htmlFor="txtAbstractBody">Abstract Body</label>
            <textarea
              id="txtAbstractBody"
              name="abstractBody"              
              className="form-control"
              rows={5}
              value={formData.abstractBody}
              onChange={handleChange}
            />
          </div>
        </fieldset>

        {/* <fieldset>
          <legend>Format Results</legend>
          <div className="form-row">
          <div className="form-group form-column">
            <label htmlFor="hits">View results per page:</label>
            <select id="hits" className="form-control" style={{ width: "70px", display: "inline-block" }}>
              <option>10</option>
              <option>25</option>
              <option>50</option>
            </select>
          </div>

          <div className="form-group form-column">
            <span>Sort:</span>
            <table className="radio-table">
              <tbody>
                <tr>
                  <td>
                    <input type="radio" name="radioSort" value="newest" defaultChecked /> Newest first
                  </td>
                  <td>
                    <input type="radio" name="radioSort" value="oldest" /> Oldest first
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>
        </fieldset> */}

        <button type="submit" className="btn btn-orange search-button">
          Search
        </button>
      </form>

      <div ref={resultsTopRef} tabIndex={-1} aria-hidden="true" />
      {total > 50 && page === 1 && !loading && !error && (
        <div className="alert alert-warning too-many-alert" role="alert" style={{ marginTop: 16 }}>
          We found <strong>{total}</strong> abstracts, which is more than the default number the system renders (100).  
          Please refine your search (add a year, author, title or keywords) to narrow the results.
        </div>
      )}

     {loading && (
        <div className="loading-wrap">
          <span className="loading-spinner fa fa-spinner fa-spin" aria-hidden="true" />
          <span className="loading-text">Loading results</span>
        </div>
      )}

      {error && (
        <div className="alert alert-warning" role="alert" style={{ marginTop: 16 }}>
          {error}
        </div>
      )}

    {submittedFilters && results.length > 0 && !loading && !error && (
  <div className="results-toolbar" style={{ marginTop: 16 }}>
    {/* LEFT: Results for … */}
    <div className="results-summary">
      <strong>Results for:</strong>
      <div className="filters-line">
        {getActiveFilters(submittedFilters).map(([label, value]) => (
          <span key={label} className="filter-chip">
            {label}: {value}
          </span>
        ))}
      </div>
    </div>

    {/* RIGHT: Sort */}
    <div className="sort-control">
      <label htmlFor="sortSelect" className="sort-label">Sort:</label>
      <select
        id="sortSelect"
        className="sort-select"
        value={sort}
        onChange={async (e) => {
          const newSort = e.target.value;
          setSort(newSort);
          if (!submittedFilters) return;

          setLoading(true);
          setError("");
          try {
            const data = await searchAbstracts({
              abstractNumber: submittedFilters.abstractNumber || "",
              year:
                submittedFilters.year &&
                submittedFilters.year !== "Select a year"
                  ? Number(submittedFilters.year)
                  : null,
              author: submittedFilters.author || "",
              title: submittedFilters.title || "",
              keywords: submittedFilters.keywords || "",
              abstractBody: submittedFilters.abstractBody || "",
              page: 1,
              hits: HITS_PER_PAGE,   // make sure this const is in scope
              sort: newSort
            });
            setResults(data.items || []);
            setTotal(data.total || 0);
            setPage(1);
          } catch (err) {
            setError(err.message || "Failed to sort.");
          } finally {
            setLoading(false);
          }
        }}
      >
        <option value="year_asc">Year Ascendent</option>
        <option value="year_desc">Year Descendent</option>
      </select>
    </div>
  </div>
)}


      {results.length > 0 && !loading && (
        <>
          {/* Results summary chips 
          <div className="results-summary">
            <strong>Results for:</strong>
            <div className="filters-line">
              {getActiveFilters(formData).map(([label, value]) => (
                <span key={label} className="filter-chip">
                  {label}: {value}
                </span>
              ))}
            </div>
          </div>*/}

          {/* Responsive results list (cards, flex/grid) */}
          <div className="results-list">
            {results.map((r) => (
              <article key={r.MEMID} className="result-card">
                <header className="result-header">
                  <h4
                    className="result-title"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeAndTitleCaseHTML(r.TITLE) || "Untitled"
                    }}
                  />
                  <div className="result-meta-right">
                    {r.ABSTRACT_YEAR ? <span className="badge badge-year">{r.ABSTRACT_YEAR}</span> : null}
                    {r.ABSTRACT_FINAL_ID ? <span className="badge badge-id">#{r.ABSTRACT_FINAL_ID}</span> : null}
                  </div>
                </header>

                <div className="result-body">
                  {r.SESSION_TYPE && (
                    <div className="meta-row">
                      <span className="meta-label">Type:</span>
                      <span
                        className="meta-value"
                        dangerouslySetInnerHTML={{ __html: sanitizeInline(r.SESSION_TYPE) }}
                      />
                    </div>
                  )}

                  {r.AUTHORS && (
                    <div className="meta-row">
                      <span className="meta-label">Author(s):</span>
                      <span
                        className="meta-value"
                        dangerouslySetInnerHTML={{ __html: sanitizeInline(r.AUTHORS) }}
                      />
                    </div>
                  )}

                  

                  {r.INSTITUTIONS && (
                    <div className="meta-row">
                      <span className="meta-label">Institution(s):</span>
                      <span
                        className="meta-value wrap-text"
                        dangerouslySetInnerHTML={{ __html: sanitizeRich(r.INSTITUTIONS) }}
                      />
                    </div>
                  )}

                  


              {(hasText(r.SESSION_TITLE) || hasText(r.KEYWORDS)) && (
                  <div className="meta-row session-keywords-row">
                    {hasText(r.SESSION_TITLE) && (
                      <div className="meta-side meta-left">
                        <span className="meta-label">Session Name:</span>
                        <span
                          className="meta-value"
                          dangerouslySetInnerHTML={{ __html: sanitizeInline(r.SESSION_TITLE) }}
                        />
                      </div>
                    )}

                    {hasText(r.KEYWORDS) && (
                      <div className="meta-side meta-right">
                        <span className="meta-label">Keywords:</span>
                        <span
                          className="meta-value"
                          dangerouslySetInnerHTML={{ __html: sanitizeInline(r.KEYWORDS) }}
                        />
                      </div>
                    )}
                  </div>
                )}


                  {r.ABSTRACT_BODY && (
                    <div className="meta-row">
                      <span className="meta-label">Abstract:</span>
                      <span
                        className="meta-value abstract-text"
                        dangerouslySetInnerHTML={{ __html: sanitizeRich(r.ABSTRACT_BODY) }}
                      />
                    </div>
                  )}

                </div>
              </article>
            ))}
          </div>
          {/* Pagination */}
          {total > HITS_PER_PAGE && (
            <div className="pagination">
              {Array.from({ length: Math.min(Math.ceil(total / HITS_PER_PAGE), 10) }, (_, i) => i + 1)
                .map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`page-btn ${page === n ? "active" : ""}`}
                    onClick={async () => {
                      if (n === page) return;
                      setLoading(true);
                      setError("");
                      try {
                        const data = await searchAbstracts({
                          // use last submitted filters (not live form edits)
                          abstractNumber: submittedFilters.abstractNumber || "",
                          year: submittedFilters.year && submittedFilters.year !== "Select a year" ? Number(submittedFilters.year) : null,
                          author: submittedFilters.author || "",
                          title: submittedFilters.title || "",
                          keywords: submittedFilters.keywords || "",
                          abstractBody: submittedFilters.abstractBody || "",
                          page: n,
                          hits: HITS_PER_PAGE,
                          sort
                        });
                        setResults(data.items || []);
                        setTotal(data.total || 0);
                        setPage(n);
                      } catch (err) {
                        setError(err.message || "Failed to load page.");
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {n}
                  </button>
                ))}
            </div>
          )}

        </>
      )}



    </div>
  );
}
